import type {ReactElement} from 'react';
import React, {useEffect} from 'react';
import {PosterNavOpts} from '@Components/poster-editor/components/poster-nav-opts';
import {PosterEditorNavBar} from '@Components/poster-editor/components/poster-editor-nav-bar';
import {AudioPlaylistItem} from '@Components/poster-editor/components/audio-playlist-item';
import {bindPosterEditorShortcuts, disablePosterEditorShortcuts, enablePosterEditorShortcuts, unbindPosterEditorShortcuts} from '@PosterWhiteboard/poster/poster-editor-shortcuts';
import {isPosterPushModalOpen} from '@Components/modal-container/modal-container-reducer';
import type {PosterOpts} from '@PosterWhiteboard/poster/poster.class';
import {PosterModeType} from '@PosterWhiteboard/poster/poster-mode.class';
import {SaveReminderNotification} from '@Components/save-reminder-notification';
import type {PosterEditorConfig} from '@Components/poster-editor/poster-editor.types';
import {EDITOR_COLLAPSABLE_SIDEBAR_THRESHOLD} from '@Components/poster-editor/poster-editor.types';
import {
  isScaleAnimatingToFitToScreen,
  updateCollapsableSidebarState,
  updateIsEmbeddedExportMode,
  updateIsPosterEditorMobileVariant,
} from '@Components/poster-editor/poster-editor-reducer';
import {watchIsAudioPlayable} from '@Utils/audio.util';
import {PosterWhiteboard} from '@Components/poster-whiteboard';
import {PosterMobileSmallSeekbar} from '@Components/poster-editor/components/poster-mobile-small-seekbar';
import {POSTER_MOBILE_BIG_SEEK_BAR_HEIGHT} from '@Components/poster-editor/components/poster-mobile-big-seekbar/poster-mobile-big-seekbar.types';
import {PosterEditorBottomBar} from '@Components/poster-editor/components/poster-editor-bottom-bar';
import {isEditorMobileVariant, pausePosterOnModalOpen} from '@Components/poster-editor/library/poster-editor-library';
import {PostPosterElement} from '@Components/poster-editor/components/post-poster-element';
import {PostPosterModalSpacer} from '@Components/poster-editor/components/post-poster-modal-spacer';
import {LoadingToast} from '@Components/loading-toast';
import {MessageGrowl} from '@Components/message-growl';
import {Snackbar} from '@Components/snackbar';
import {SnackbarMessageGrowl} from '@Components/snackbar-message-growl';
import {getRecentStockItems} from '@Libraries/pmw-stock-media-library';
import {setRecentStockAvailability} from '@Panels/stock-panel/stock-panel-reducer';
import {MIN_STOCK_ITEMS_TO_DISPLAY} from '@Panels/stock-panel/stock-panel.types';
import {usePosterEditorPrefetchModules} from '@Hooks/poster-editor/usePosterEditorPrefetchModules';
import {CanvasZoomPinchOnboarding} from '@Components/poster-whiteboard/components/canvas-zoom-pinch-onboarding';
import {useEditorMobileOnboarding} from '@Hooks/poster-editor/useEditorMobileOnboarding';
import {PosterEditorWebBottomBar} from '@Components/poster-editor/components/poster-editor-web-bottom-bar';
import useWindowSize from '@Hooks/useWindowSize';
import {PosterEditingNavbarMode} from '@Components/poster-editor/components/poster-editor-nav-bar/poster-editor-nav-bar.types';
import {PosterEditorErrorBoundary} from '@Components/poster-editor/components/poster-editing-side-panel/components/poster-editor-error-boundary';
import useToggleLoadingNotificationOnPanelOpenClose from '@Hooks/useToggleLoadingNotificationOnPanelOpenClose';
import {AI_SUBTITLE_PANEL_ID} from '@Panels/ai-subtitle-panel/ai-subtitle-panel.types';
import {AI_SUBTITLES_GENERATION_LOADING_KEY} from '@Panels/ai-subtitle-panel/components/ai-subtitle-generating/ai-subtitle-generating.types';
import {AiSubtitleState} from '@Components/poster-editor/components/poster-ai/poster-ai.types';
import styles from './poster-editor.module.scss';
import {PosterAddItemBar} from './components/poster-add-item-bar';
import {PosterEditingSidePanel} from './components/poster-editing-side-panel';
import {useAppDispatch, useAppSelector} from '@/hooks';
// needed in download-publish-dialog.js.
import '@ThirdParty/modernizr.custom.24530';
import '@Global/download-publish-dialog';
import '@Global/message-growl';
// needed in download-publish-dialog.js to open download modal.
import '@Global/modal';
// needed in download-publish-dialog.js.
import '@Global/loading-toast';
import {CursorOverlay} from '@Components/poster-editor/components/cursor-overlay';
import {InitalPosterItemsLoadErrorNotification} from '../inital-poster-items-load-error/inital-poster-items-load-error-notification';
import {EmbeddedEditorMessageType} from '@Libraries/embedded-editor';
import {TimelineMobileOnboarding} from '@Components/poster-editor/components/poster-editor-bottom-bar/components/timeline-mobile-onboarding/timeline-mobile-onboarding';
import {updateUserPreferences} from '@/reducers/user-preferences.reducer';

window.isPosterMaker = true;
window.posterEditor = {
  whiteboard: undefined,
  config: undefined,
  elements: {},
};

export interface PosterEditorProps {
  className?: string;
  posterOpts: PosterOpts;
  posterEditorConfig: PosterEditorConfig;
  isEmbedded?: boolean;
}

export function PosterEditor({className = '', isEmbedded = false, ...props}: PosterEditorProps): ReactElement | null {
  const isMobileVariant =
    useAppSelector((state) => {
      return state.posterEditor.isMobileVariant;
    }) || false;
  const isScaleAnimatingToFit = useAppSelector(isScaleAnimatingToFitToScreen);
  const isPosterVideo = useAppSelector((state) => {
    return state.posterEditor.isPosterVideo;
  });
  const canvasHeight = useAppSelector((state) => {
    return state.posterEditor.canvasDimensions.height;
  });
  const whiteboardContainerHeight = useAppSelector((state) => {
    return state.posterEditor.whiteboardContainerDimensions.height;
  });
  const isSidebarSmall = useAppSelector((state) => {
    return state.posterEditor.isSidebarSmall;
  });
  const aiSubtitleState = useAppSelector((state) => {
    return state.posterEditor.aiState.aiSubtitles;
  });
  const aiSubtitlesReduxState = useAppSelector((state) => {
    return state.aiSubtitle;
  });

  const {windowWidth} = useWindowSize();
  const isPosterPushModalOpened = useAppSelector(isPosterPushModalOpen);
  const dispatch = useAppDispatch();
  usePosterEditorPrefetchModules();

  useToggleLoadingNotificationOnPanelOpenClose({
    panelId: AI_SUBTITLE_PANEL_ID,
    loadingKey: AI_SUBTITLES_GENERATION_LOADING_KEY,
    loadingText: `${window.i18next.t('pmwjs_generating')} (${aiSubtitlesReduxState.generationsBatch.currentNumberOfCompletedGenerations} of ${aiSubtitlesReduxState.generationsBatch.totalItemsToGenerateSubtitlesFor})`,
    showLoadingState: aiSubtitleState === AiSubtitleState.GENERATING,
  });

  useEditorMobileOnboarding({
    showOnboardingFromConfig: props.posterEditorConfig.showMobileUXOnBoarding,
  });

  const showMobileEditor = (): boolean => {
    return isEditorMobileVariant();
  };

  const initializeRecentStockItemFlag = async (): Promise<void> => {
    try {
      const response = await getRecentStockItems();
      dispatch(setRecentStockAvailability(response && response.length >= MIN_STOCK_ITEMS_TO_DISPLAY));
    } catch (e) {
      dispatch(setRecentStockAvailability(false));
    }
  };

  function handlePaste(e: ClipboardEvent): void {
    const target = e.target as HTMLElement | null;

    const isEditable = target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement || target?.isContentEditable;
    if (isPosterPushModalOpened || isEditable) {
      return;
    }

    void window.posterEditor?.whiteboard?.getCurrentPage().clipboard.pasteItems(e);
  }

  useEffect(() => {
    if (window.posterEditor) {
      window.posterEditor.config = props.posterEditorConfig;
    }
    dispatch(updateUserPreferences(props.posterEditorConfig.userPreferences));
    watchIsAudioPlayable();
    initComWithIframe();
    window.addEventListener('paste', handlePaste);
    bindPosterEditorShortcuts();
    void initializeRecentStockItemFlag();

    return (): void => {
      window.removeEventListener('paste', handlePaste);
      unbindPosterEditorShortcuts();
    };
  }, []);

  useEffect(() => {
    if (isPosterPushModalOpened) {
      disablePosterEditorShortcuts();
      pausePosterOnModalOpen();
    } else {
      enablePosterEditorShortcuts();
    }
  }, [isPosterPushModalOpened]);

  const isSidebarCollapsable = (): boolean => {
    return windowWidth < EDITOR_COLLAPSABLE_SIDEBAR_THRESHOLD;
  };

  useEffect(() => {
    dispatch(updateIsPosterEditorMobileVariant(showMobileEditor()));
  }, []);

  useEffect(() => {
    dispatch(updateCollapsableSidebarState(isSidebarCollapsable()));
  }, [windowWidth]);

  const getNavBar = (): ReactElement => {
    const mode = window.posterEditor?.whiteboard?.mode;
    if (isEmbedded) {
      return <PosterEditorNavBar mode={PosterEditingNavbarMode.EMBEDDED} />;
    }
    switch (mode?.details.type) {
      case PosterModeType.REGEN:
        return <PosterEditorNavBar mode={PosterEditingNavbarMode.REGEN} />;
      default:
        return <PosterEditorNavBar />;
    }
  };

  const getEditorContainerClasses = (): string => {
    return `${styles.editorWithoutNav} ${styles.dottedBg} ${isMobileVariant ? styles.editorMobile : ''}`;
  };

  const shouldShowSmallMobileSeekbar = (): boolean => {
    return !isScaleAnimatingToFit && isMobileVariant && canvasHeight + POSTER_MOBILE_BIG_SEEK_BAR_HEIGHT > whiteboardContainerHeight;
  };

  const isPosterOverlappingWithBottomSheet = (): boolean => {
    return isMobileVariant && canvasHeight + 16 > whiteboardContainerHeight;
  };

  const getPosterWhiteboard = (): ReactElement => {
    return (
      <PosterEditorErrorBoundary>
        <PosterWhiteboard
          isMobileDisplay={showMobileEditor()}
          postWhiteboardElement={<PostPosterElement />}
          postWhiteboardContainerElement={shouldShowSmallMobileSeekbar() && isPosterVideo ? <PosterMobileSmallSeekbar /> : null}
          isCenterAligned
          posterOpts={props.posterOpts}
        />
      </PosterEditorErrorBoundary>
    );
  };

  const getMobileVariant = (): ReactElement => {
    return (
      <div className={getEditorContainerClasses()}>
        <CursorOverlay />
        <PosterWhiteboard
          isMobileDisplay
          postWhiteboardElement={<PostPosterElement />}
          postWhiteboardContainerElement={shouldShowSmallMobileSeekbar() && isPosterVideo ? <PosterMobileSmallSeekbar /> : null}
          isCenterAligned
          posterOpts={props.posterOpts}
        />
        <Snackbar />
        <SnackbarMessageGrowl />
        <PostPosterModalSpacer />
        <React.StrictMode>
          <PosterEditorBottomBar isBottomSheetOverlapping={isPosterOverlappingWithBottomSheet()} />
        </React.StrictMode>
      </div>
    );
  };

  const getWebVariant = (): ReactElement => {
    return (
      <div className={getEditorContainerClasses()}>
        <div className={`flex flex-column-align-center _full-height _full-width ${!isSidebarSmall ? styles.maxWidth : ''}`}>
          <div className={`flex flex-row-align-center _full-width ${styles.transitionAccordingToTimeline} _full-height`}>
            <React.StrictMode>
              <PosterAddItemBar />
            </React.StrictMode>
            <CursorOverlay />
            {getPosterWhiteboard()}
          </div>
          <React.StrictMode>
            <PosterEditorWebBottomBar />
          </React.StrictMode>
        </div>
        <React.StrictMode>
          <PosterEditingSidePanel />
        </React.StrictMode>
      </div>
    );
  };

  return (
    <>
      <div className={`${styles.editor} ${className}`}>
        <React.StrictMode>{getNavBar()}</React.StrictMode>
        {isMobileVariant ? getMobileVariant() : getWebVariant()}
        <React.StrictMode>
          {isMobileVariant ? null : <PosterNavOpts />}
          {!isMobileVariant ? <SaveReminderNotification /> : null}
          <InitalPosterItemsLoadErrorNotification />
          {!isMobileVariant ? <AudioPlaylistItem /> : null}
        </React.StrictMode>
      </div>
      {!isMobileVariant ? <LoadingToast /> : null}
      {!isMobileVariant ? <MessageGrowl /> : null}
      {isMobileVariant ? <CanvasZoomPinchOnboarding /> : null}
      {isMobileVariant ? <TimelineMobileOnboarding /> : null}
    </>
  );
}

const initComWithIframe = (): void => {
  window.addEventListener('message', (e): void => {
    if (e && typeof e.data === 'string') {
      const parts = e.data.split('~');
      // length === 2 such that 1 = key, 2 = value.
      if (parts.length === 2) {
        const key = parts[0];
        const value = parts[1];

        switch (key) {
          case 'Mode':
            if (value === 'ReturnURL') {
              window.PMW.redux.store.dispatch(updateIsEmbeddedExportMode(true));
            }
            break;

          default:
            break;
        }
      }
    }
  });
  window.parent.postMessage(EmbeddedEditorMessageType.RENDER_CLOSE_BTN, '*');
  window.parent.postMessage(EmbeddedEditorMessageType.READY_FOR_MODE, '*');
};
